// Kho 12 logo + 8 khung «Huy hiệu đội» bản nhẹ của tiểu đội Phòng KD (QĐ 0485, CEO 2b · 5a ·
// góp ý 28/10/2026 «tối giản nhẹ lại»). Tranh do Codex vẽ, nằm sẵn trong ứng dụng
// (`assets/logo-doi`, `assets/khung-doi`). Mã khớp `backend/internal/core/salessquad/catalog.go`
// — bài `catalog.test.ts` đọc tệp Go và đỏ khi hai bên lệch nhau.

export type LogoPresetCode =
  | "sen-hong"
  | "mai-vang"
  | "hac-trang"
  | "buom-xanh"
  | "sao-mai"
  | "trang-non"
  | "binh-minh"
  | "choi-xanh"
  | "song-ngoc"
  | "may-hong"
  | "ca-chep"
  | "thien-nga";

/** 12 logo kho theo thứ tự hiện ở hộp đổi logo; nhãn là tên tranh (bản mẫu 3). */
export const LOGO_PRESETS: readonly { code: LogoPresetCode; label: string }[] = [
  { code: "sen-hong", label: "Hoa sen" },
  { code: "mai-vang", label: "Hoa mai" },
  { code: "hac-trang", label: "Chim hạc" },
  { code: "buom-xanh", label: "Cánh bướm" },
  { code: "sao-mai", label: "Ngôi sao" },
  { code: "trang-non", label: "Trăng non" },
  { code: "binh-minh", label: "Bình minh" },
  { code: "choi-xanh", label: "Chồi non" },
  { code: "song-ngoc", label: "Sóng nước" },
  { code: "may-hong", label: "Áng mây" },
  { code: "ca-chep", label: "Cá chép" },
  { code: "thien-nga", label: "Thiên nga" },
];

export type FrameCode =
  | "giu-ngoi-vang"
  | "giu-ngoi-bac"
  | "giu-ngoi-dong"
  | "ky-niem-thang-vang"
  | "ky-niem-thang-bac"
  | "ky-niem-thang-dong"
  | "dat-muc-tieu"
  | "ba-thang-lien";

export type FrameGroup = "reign" | "monthly" | "target" | "streak";
export type Metal = "vang" | "bac" | "dong";

export type FrameDef = {
  code: FrameCode;
  group: FrameGroup;
  metal: Metal;
  ribbon: "hong" | "lam" | "luc" | "tim";
  tier: number;
  label: string;
  /** Điều kiện có khung — chữ ở tủ khung. */
  how: string;
  /** Giữ ngôi rớt hạng là tháo; các món còn lại giữ vĩnh viễn. */
  keep: "Đeo tạm" | "Giữ vĩnh viễn";
};

/** 8 khung theo thứ tự tủ khung (bản mẫu 6). */
export const FRAMES: readonly FrameDef[] = [
  {
    code: "giu-ngoi-vang",
    group: "reign",
    metal: "vang",
    ribbon: "hong",
    tier: 1,
    label: "Giữ ngôi · Vàng",
    how: "Đang đứng hạng 1 bảng tiểu đội tháng đang chạy",
    keep: "Đeo tạm",
  },
  {
    code: "giu-ngoi-bac",
    group: "reign",
    metal: "bac",
    ribbon: "hong",
    tier: 2,
    label: "Giữ ngôi · Bạc",
    how: "Đang đứng hạng 2",
    keep: "Đeo tạm",
  },
  {
    code: "giu-ngoi-dong",
    group: "reign",
    metal: "dong",
    ribbon: "hong",
    tier: 3,
    label: "Giữ ngôi · Đồng",
    how: "Đang đứng hạng 3",
    keep: "Đeo tạm",
  },
  {
    code: "ky-niem-thang-vang",
    group: "monthly",
    metal: "vang",
    ribbon: "lam",
    tier: 1,
    label: "Kỷ niệm tháng · Vàng",
    how: "Tháng chốt lương ở hạng 1 — ghi tháng lên khung",
    keep: "Giữ vĩnh viễn",
  },
  {
    code: "ky-niem-thang-bac",
    group: "monthly",
    metal: "bac",
    ribbon: "lam",
    tier: 2,
    label: "Kỷ niệm tháng · Bạc",
    how: "Tháng chốt lương ở hạng 2",
    keep: "Giữ vĩnh viễn",
  },
  {
    code: "ky-niem-thang-dong",
    group: "monthly",
    metal: "dong",
    ribbon: "lam",
    tier: 3,
    label: "Kỷ niệm tháng · Đồng",
    how: "Tháng chốt lương ở hạng 3",
    keep: "Giữ vĩnh viễn",
  },
  {
    code: "dat-muc-tieu",
    group: "target",
    metal: "vang",
    ribbon: "luc",
    tier: 0,
    label: "Đạt mục tiêu tháng",
    how: "Tháng chốt lương ≥ 100% mục tiêu",
    keep: "Giữ vĩnh viễn",
  },
  {
    code: "ba-thang-lien",
    group: "streak",
    metal: "vang",
    ribbon: "tim",
    tier: 0,
    label: "Ba tháng liền đạt",
    how: "3 tháng liền ≥ 100% mục tiêu",
    keep: "Giữ vĩnh viễn",
  },
];

export function frameDef(code: string | null | undefined): FrameDef | undefined {
  return FRAMES.find((f) => f.code === code);
}

// Hình học tranh khung (đo bằng `_giao-CEO/28-10-tieu-doi-ban-mau/khung-nhe/build_assets.py`,
// % cạnh tranh 512px): lỗ giữa tâm 50 · 50, bán kính HOLE_R; top/bottom/left/right = mép phần có
// hình (ngoài đó trong suốt — cắt bằng lề âm cho khung không đẩy chữ quanh nó); ribbon = ô chữ
// an toàn trên ruy băng. Vẽ lại khung ⇒ đo lại, sửa bảng này.
export const HOLE_R = 31.0547;

export type SquadFrameGeometry = {
  top: number;
  bottom: number;
  left: number;
  right: number;
  ribbon: { x: number; y: number; w: number; h: number };
};

export const SQUAD_FRAME_GEOMETRY: Record<FrameCode, SquadFrameGeometry> = {
  "giu-ngoi-vang": {
    top: 9.18,
    bottom: 88.87,
    left: 12.3,
    right: 87.79,
    ribbon: { x: 27.15, y: 79.39, w: 45.7, h: 8.5 },
  },
  "giu-ngoi-bac": {
    top: 9.18,
    bottom: 89.16,
    left: 12.11,
    right: 87.99,
    ribbon: { x: 26.95, y: 79.49, w: 45.61, h: 8.69 },
  },
  "giu-ngoi-dong": {
    top: 8.98,
    bottom: 89.26,
    left: 12.01,
    right: 87.99,
    ribbon: { x: 27.15, y: 79.49, w: 45.61, h: 8.5 },
  },
  "ky-niem-thang-vang": {
    top: 10.35,
    bottom: 89.36,
    left: 12.01,
    right: 87.89,
    ribbon: { x: 27.05, y: 79.69, w: 45.61, h: 8.5 },
  },
  "ky-niem-thang-bac": {
    top: 10.64,
    bottom: 89.06,
    left: 11.91,
    right: 88.09,
    ribbon: { x: 27.05, y: 79.3, w: 45.7, h: 8.59 },
  },
  "ky-niem-thang-dong": {
    top: 10.35,
    bottom: 89.26,
    left: 12.11,
    right: 87.99,
    ribbon: { x: 27.44, y: 79.49, w: 45.21, h: 8.5 },
  },
  "dat-muc-tieu": {
    top: 9.96,
    bottom: 88.87,
    left: 12.3,
    right: 87.7,
    ribbon: { x: 27.15, y: 79.2, w: 45.51, h: 8.59 },
  },
  "ba-thang-lien": {
    top: 11.72,
    bottom: 89.36,
    left: 12.11,
    right: 87.89,
    ribbon: { x: 27.25, y: 79.69, w: 45.02, h: 8.59 },
  },
};

function theoTen(files: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [path, url] of Object.entries(files)) {
    const ten = path
      .split("/")
      .pop()
      ?.replace(/\.webp$/, "");
    if (ten) out[ten] = url;
  }
  return out;
}

const LOGO_ART = theoTen(
  import.meta.glob<string>("../../assets/logo-doi/*.webp", {
    eager: true,
    import: "default",
    query: "?url",
  }),
);
const FRAME_ART = theoTen(
  import.meta.glob<string>("../../assets/khung-doi/*.webp", {
    eager: true,
    import: "default",
    query: "?url",
  }),
);

/** Ảnh logo kho; mã lạ (máy chủ mới hơn màn) ⇒ rỗng, logo lùi về chữ cái đầu. */
export function presetArt(code: string | null | undefined): string {
  return (code && LOGO_ART[code]) || "";
}

/** Ảnh tranh khung đủ. */
export function frameArt(code: FrameCode): string {
  return FRAME_ART[code] ?? "";
}

/** Khung bục / tủ khung theo hạng: 1 vàng · 2 bạc · 3 đồng. */
export function tierMetal(tier: number): Metal {
  return tier <= 1 ? "vang" : tier === 2 ? "bac" : "dong";
}

/** "2026-11" → "11/2026". */
export function monthLabel(month: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month ?? "");
  return m ? `${m[2]}/${m[1]}` : "";
}

/** Chữ in trên ruy băng của tranh khung đủ (bản mẫu 6). */
export function ribbonText(code: FrameCode, month?: string | null, rank?: number | null): string {
  const def = frameDef(code);
  if (!def) return "";
  switch (def.group) {
    case "reign":
      return `HẠNG ${rank || def.tier}`;
    case "monthly":
      return month ? `T${monthLabel(month)}` : `HẠNG ${rank || def.tier}`;
    case "target":
      return month ? `ĐẠT ${monthLabel(month)}` : "ĐẠT MỤC TIÊU";
    case "streak":
      return "3 THÁNG LIỀN";
  }
}

/** "Tiểu đội SG4" → "SG"; "Hạc Trắng" → "HT". */
export function squadInitials(name: string): string {
  const full = name.normalize("NFC").trim();
  const rest = full.replace(/^tiểu\s+đội\s*/iu, "");
  if (rest !== full) {
    const first = rest.split(/\s+/).find(Boolean);
    return first ? first.slice(0, 2).toUpperCase() : "TĐ";
  }
  const words = full.split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  if (words.length === 0) return "TĐ";
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}
